// Loads the Onfido Web SDK from Onfido's CDN at runtime
export const ONFIDO_FACIAL_SCAN_POLICY_URL =
  'https://www.entrust.com/legal-compliance/data-privacy/facial-scan-policy-and-release';
export const ONFIDO_TERMS_OF_SERVICE_URL =
  'https://www.entrust.com/legal-compliance/terms-conditions/idv/terms-of-service/';

const ONFIDO_SDK_SRC = 'https://sdk.onfido.com/v14';
const ONFIDO_SCRIPT_ID = 'onfido-sdk';

export interface OnfidoSdkHandle {
  tearDown: () => Promise<void> | void;
}

export interface OnfidoInitOptions {
  token: string;
  workflowRunId: string;
  containerId: string;
  language?: string;
  onComplete?: (data: unknown) => void;
  onError?: (error: unknown) => void;
}

export interface OnfidoGlobal {
  init: (options: OnfidoInitOptions) => OnfidoSdkHandle;
}

declare global {
  interface Window {
    Onfido?: OnfidoGlobal;
  }
}

const ONFIDO_MOCK_ENABLED = process.env.REACT_APP_ONFIDO_MOCK === 'true';

export const MOCK_OUTCOMES = ['APPROVED', 'DECLINED', 'REVIEW'] as const;

export interface MockCompleteData {
  mockOutcome?: typeof MOCK_OUTCOMES[number];
}

const createMockOnfido = (): OnfidoGlobal => ({
  init: options => {
    const container = document.getElementById(options.containerId);
    if (!container) {
      throw new Error(`Mock Onfido: container #${options.containerId} not found`);
    }
    container.innerHTML = '';

    const panel = document.createElement('div');
    panel.setAttribute('data-testid', 'mock-onfido-sdk');
    panel.setAttribute(
      'style',
      'border: 2px dashed #092FF4; border-radius: 8px; padding: 16px; display: flex; flex-direction: column; gap: 12px; align-items: center;',
    );

    const heading = document.createElement('p');
    heading.textContent = `TEST MODE — Onfido SDK stubbed (run ${options.workflowRunId})`;
    panel.appendChild(heading);

    const addButton = (label: string, onClick: () => void) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = label;
      button.addEventListener('click', onClick);
      panel.appendChild(button);
    };
    // The run's outcome decides what ops sees — approved leaves verified
    // documents, declined and review send them to an analyst — so the tester
    // picks it here and OnfidoStudio forwards it to django. Values are the
    // OnfidoStudioMockOutcome enum members.
    MOCK_OUTCOMES.forEach(outcome =>
      addButton(
        `Complete as ${outcome.toLowerCase()}`,
        () => options.onComplete && options.onComplete({ mockOutcome: outcome }),
      ),
    );
    addButton(
      'Fail verification',
      () => options.onError && options.onError(new Error('mock-sdk-error')),
    );

    container.appendChild(panel);

    return {
      tearDown: () => {
        container.innerHTML = '';
      },
    };
  },
});

let sdkPromise: Promise<OnfidoGlobal> | null = null;

export const loadOnfidoSdk = (): Promise<OnfidoGlobal> => {
  if (ONFIDO_MOCK_ENABLED) {
    if (!sdkPromise) {
      sdkPromise = Promise.resolve(createMockOnfido());
    }
    return sdkPromise;
  }

  if (!sdkPromise) {
    sdkPromise = new Promise<OnfidoGlobal>((resolve, reject) => {
      if (window.Onfido) {
        resolve(window.Onfido);
        return;
      }

      const existing = document.getElementById(ONFIDO_SCRIPT_ID);
      if (existing) {
        existing.remove();
      }

      const script = document.createElement('script');
      script.id = ONFIDO_SCRIPT_ID;
      script.src = ONFIDO_SDK_SRC;
      script.charset = 'utf-8';
      script.onload = () => {
        if (window.Onfido) {
          resolve(window.Onfido);
        } else {
          reject(new Error('Onfido SDK script loaded but window.Onfido is missing'));
        }
      };
      script.onerror = () => {
        reject(new Error('Failed to load the Onfido SDK script'));
      };
      document.body.appendChild(script);
    });

    // A failed load must not be cached, so a retry can re-inject the script.
    sdkPromise = sdkPromise.catch(error => {
      sdkPromise = null;
      throw error;
    });
  }

  return sdkPromise;
};
